import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import axios from '../services/ApiService';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';

import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
interface MediaLibraryDialogProps {
    open: boolean;
    onClose: () => void;
    onSelect: (designs: any[]) => void; 
    multiSelect?: boolean;
}

interface MediaLibraryItem {
    id: string;
    title: string;
    thumbnailUrl: string;
    url: string;
    type?: string;
    [key: string]: any;
}

interface FiltersState {
    page: number;
    pageSize: number;
    search: string;
    type: string;
    source: string;
}

const MEDIA_TYPES = [
    { label: 'All Types', value: '' },
    { label: 'Image', value: 'image' },
    { label: 'Video', value: 'video' },
    { label: 'PDF', value: 'pdf' }
];
const SOURCES = [
    { label: 'All Sources', value: '' },
    { label: 'Canva', value: 'canva' },
    { label: 'Pexels', value: 'pexels' },
    /*...*/
];

// Helper: returns an icon based on file type
function getTypeIcon(type: string, ext: string, size: number = 24) {
    if (type === "video" || ext === "mp4") return <PlayCircleOutlineIcon sx={{ color: "#007aff", fontSize: size }} />;
    if (type === "pdf" || ext === "pdf") return <PictureAsPdfIcon sx={{ color: "#e34f3a", fontSize: size }} />;
    // Default: image
    return <InsertPhotoIcon sx={{ color: "#2086F6", fontSize: size }} />;
}

// Helper: returns extension from url or filename
function getExtFromUrlOrType(item: MediaLibraryItem): string {
    if (item.type && item.type !== "image") return item.type;
    if (item.url) {
        const m = item.url.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
        return m ? m[1].toLowerCase() : "img";
    }
    return "img";
}

// Helper: returns main type ("image" | "video" | "pdf" | ...)
function getMainType(item: MediaLibraryItem): string {
    if (item.type) return item.type;
    const ext = getExtFromUrlOrType(item);
    if (["mp4", "mov", "webm"].includes(ext)) return "video";
    if (["pdf"].includes(ext)) return "pdf";
    return "image";
}

const MediaLibraryDialog: React.FC<MediaLibraryDialogProps> = ({ open, onClose, onSelect, multiSelect = true }) => {
    const [loading, setLoading] = useState(false);
    const [libraryItems, setLibraryItems] = useState<MediaLibraryItem[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const [filters, setFilters] = useState<FiltersState>({
        page: 1,
        pageSize: 24,
        search: '',
        type: '',
        source: ''
    });

    const handleFilterChange = (field: keyof FiltersState, value: string | number) => {
        setFilters(prev => ({ ...prev, [field]: value, page: field === 'page' ? Number(value) : 1 }));
    };

    
    useEffect(() => {
        if (open) {
            setLoading(true);
            axios.post('/mediaLibrary/list', {
                Page: filters.page,
                PageSize: filters.pageSize,
                Search: filters.search,
                Type: filters.type,
                Source: filters.source
            })
                .then(res => setLibraryItems(res.data || []))
                .catch(() => setLibraryItems([]))
                .finally(() => setLoading(false));
        }
    }, [open, filters]);

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) =>
            multiSelect
                ? prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                : prev[0] === id ? [] : [id]
        );
    };

    const handleConfirm = () => {
        const selectedDesigns = libraryItems.filter(item => selectedIds.includes(item.id));
        onSelect(selectedDesigns);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 1, pl: 3 }}>
                <LibraryAddIcon sx={{ mr: 1, color: "#2086F6" }} />
                Select From Library
                <IconButton
                    sx={{ ml: 'auto' }}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, background: "rgba(245,250,255,0.75)" }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress />
                    </Box>
                ) : libraryItems.length === 0 ? (
                    <Box textAlign="center" py={7}>
                        <Typography>No saved designs found.</Typography>
                    </Box>
                    ) : (
                        <>
                    <Paper
                      elevation={1}
                      sx={{
                        mb: 2,
                        p: 2,
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        background: "rgba(255,255,255,0.9)",
                        borderRadius: 3,
                        boxShadow: '0 1.5px 8px 0 #c7dbf72b'
                      }}
                    >
                      <TextField
                        size="small"
                        placeholder="Search by title..."
                        value={filters.search}
                        onChange={e => handleFilterChange('search', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon color="action" />
                            </InputAdornment>
                          )
                        }}
                        sx={{ minWidth: 180, flex: 1 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 110 }}>
                        <Select
                          value={filters.type}
                          onChange={e => handleFilterChange('type', e.target.value)}
                          displayEmpty
                        >
                          {MEDIA_TYPES.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 110 }}>
                        <Select
                          value={filters.source}
                          onChange={e => handleFilterChange('source', e.target.value)}
                          displayEmpty
                        >
                          {SOURCES.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 90 }}>
                        <Select
                          value={filters.pageSize}
                          onChange={e => handleFilterChange('pageSize', Number(e.target.value))}
                          displayEmpty
                        >
                          {[12, 24, 48, 96].map(n => (
                            <MenuItem key={n} value={n}>{n} / page</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Paper>
                    <Grid container spacing={2}>
                                    {libraryItems.map(item => {
                                        const ext = getExtFromUrlOrType(item);
                                        const type = getMainType(item);
                                        return (
                                            <Grid item xs={6} sm={4} md={3} key={item.id}>
                                                <Box
                                                    onClick={() => toggleSelect(item.id)}
                                                    sx={{
                                                        border: selectedIds.includes(item.id)
                                                            ? "2.5px solid #2086F6" : "1.2px solid #e4eaf7",
                                                        borderRadius: 3,
                                                        overflow: "hidden",
                                                        cursor: "pointer",
                                                        background: "rgba(255,255,255,0.85)",
                                                        transition: "border .13s, box-shadow .13s",
                                                        position: "relative",
                                                        boxShadow: selectedIds.includes(item.id)
                                                            ? "0 4px 28px #2086f633" : "0 2px 14px #b5c9de1b"
                                                    }}
                                                >
                                                    <Box sx={{ position: "relative", width: "100%" }}>
                                                        <img
                                                            src={item.thumbnailUrl}
                                                            alt={item.title}
                                                            style={{
                                                                width: "100%",
                                                                aspectRatio: "1/1",
                                                                objectFit: "cover",
                                                                background: "#ecf2fa"
                                                            }}
                                                        />
                                                        {/* Type Badge */}
                                                        <Box
                                                            className="typeBadge"
                                                            sx={{
                                                                position: "absolute",
                                                                top: 9,
                                                                left: 10,
                                                                bgcolor: "#fff",
                                                                px: 0.8,
                                                                py: 0.5,
                                                                borderRadius: 2,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 0.7,
                                                                boxShadow: "0 2px 8px #b7d3ec2a",
                                                                opacity: 0.82,
                                                                transition: "all .18s",
                                                                fontWeight: 700,
                                                                fontSize: 13,
                                                                letterSpacing: 0.1
                                                            }}
                                                        >
                                                            {getTypeIcon(type, ext, 20)}
                                                            <span style={{ marginLeft: 6, fontWeight: 600, textTransform: "uppercase" }}>
                                                                {ext}
                                                            </span>
                                                        </Box>
                                                        {selectedIds.includes(item.id) && (
                                                            <CheckCircleIcon
                                                                sx={{
                                                                    position: "absolute", top: 9, right: 10, fontSize: 32,
                                                                    color: "#2086F6", background: "#fff", borderRadius: "50%",
                                                                    zIndex: 2,
                                                                    boxShadow: "0 2px 12px #b7d3ec2c"
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Typography sx={{
                                                        px: 1.2, pt: 1, pb: 1.2,
                                                        fontWeight: 700, fontSize: "1.04rem",
                                                        color: "#243d62", textAlign: "center",
                                                        whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden"
                                                    }}>
                                                        {item.title}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        )
                                    })}
                    </Grid>
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1} gap={2}>
                         <Button
                           size="small"
                           variant="outlined"
                           onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                           disabled={filters.page <= 1}
                           sx={{ borderRadius: 2, minWidth: 36 }}
                         >
                           Prev
                         </Button>
                         <Typography variant="body2" sx={{ fontWeight: 600 }}>
                           Page {filters.page}
                         </Typography>
                         <Button
                           size="small"
                           variant="outlined"
                           onClick={() => handleFilterChange('page', filters.page + 1)}
                           sx={{ borderRadius: 2, minWidth: 36 }}
                           // Optionally: disable when no more items (libraryItems.length < pageSize)
                         >
                           Next
                         </Button>
                        </Box>
                        </>

                )}
            </DialogContent>
            <DialogActions sx={{ pb: 2, pr: 3, pl: 3 }}>
                <Button onClick={onClose} color="secondary" variant="text">Cancel</Button>
                <Button
                    onClick={handleConfirm}
                    disabled={selectedIds.length === 0}
                    variant="contained"
                    sx={{ background: "#2086F6", color: "#fff", borderRadius: 2.3, px: 3, fontWeight: 700 }}
                >
                    Select {multiSelect ? 'Designs' : 'Design'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MediaLibraryDialog;
